import DOMPurify from "dompurify";

const ALLOWED_TAGS = [
  "a",
  "b",
  "blockquote",
  "br",
  "code",
  "div",
  "em",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "hr",
  "i",
  "li",
  "ol",
  "p",
  "pre",
  "s",
  "span",
  "strong",
  "sub",
  "sup",
  "table",
  "tbody",
  "td",
  "th",
  "thead",
  "tr",
  "u",
  "ul",
];

// `[` / `{` allow unresolved merge-field placeholders like `[contact.website]`
// or `{{customer.name}}` to survive as-is; they're inert as URLs (browsers
// don't recognise the "scheme"), and the templating layer resolves them
// server-side before delivery.
const ALLOWED_URI_REGEXP = /^(?:https?|mailto|tel|\/|#|\[|\{)/i;

const BASE_CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_URI_REGEXP,
  FORBID_ATTR: ["style"],
};

// Already-safe schemes we don't want to touch. Leading `[` / `{` guard
// interpolation placeholders like `{{merge_field}}` in email/consent templates
// from being rewritten to `https://{{merge_field}}`.
const SAFE_HREF_REGEXP = /^(?:https?:\/\/|\/\/|mailto:|tel:|[[{])/i;

// Bare hostnames like `example.com` or `sub.example.co.uk/path` — we upgrade
// these to `https://…`. Anything else (unrecognised scheme like `javascript:`,
// `data:`, `vbscript:`) is left untouched so DOMPurify's `ALLOWED_URI_REGEXP`
// strips the href entirely.
const BARE_DOMAIN_REGEXP =
  /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+(?:[/?#].*)?$/i;

export const sanitizeRichText = (html: string | null | undefined): string => {
  if (!html) {
    return "";
  }

  const pre = new DOMParser().parseFromString(html, "text/html");

  pre.querySelectorAll("a").forEach((link) => {
    const href = link.getAttribute("href");
    if (!href) {
      return;
    }
    if (SAFE_HREF_REGEXP.test(href)) {
      return;
    }
    if (BARE_DOMAIN_REGEXP.test(href)) {
      link.setAttribute("href", `https://${href}`);
    }
  });

  const clean = DOMPurify.sanitize(pre.body.innerHTML, BASE_CONFIG);

  // DOMPurify strips `target` on anchors by default (anti-tabnabbing) even with
  // `ADD_ATTR`, so we enforce target/rel on surviving anchors in a post-pass.
  const post = new DOMParser().parseFromString(clean, "text/html");
  post.querySelectorAll("a[href]").forEach((link) => {
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
  });

  return post.body.innerHTML;
};
